class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    const closeButton = this.querySelector('button[type="button"]');
    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });
    }

    this.summaryToggle.setAttribute('role', 'button');
    this._isClosing = false;
    this._skipScrollLock = this.hasAttribute('data-jgt-no-scroll-lock');
    this._panelAnimate = this.hasAttribute('data-jgt-panel-animate') || this.hasAttribute('data-jgt-search-animate');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  _getPanel() {
    return this.detailsContainer.querySelector('.search-modal')
      || this.detailsContainer.querySelector('.account-modal');
  }

  _canAnimate() {
    return this._panelAnimate
      && typeof Element.prototype.animate === 'function'
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  open(event) {
    this._isClosing = false;
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);

    if (!this._skipScrollLock) {
      window.JgtScrollLock.lock();
    }

    if (this._canAnimate()) {
      const panel = this._getPanel();
      if (panel) {
        panel.getAnimations().forEach((a) => a.cancel());
        panel.animate(
          [
            { opacity: 0, transform: 'translateY(-12px) scaleY(0.92) scaleX(0.98)', clipPath: 'inset(0 0 100% 0 round 0 0 2px 2px)' },
            { opacity: 0.6, transform: 'translateY(2px) scaleY(1.01) scaleX(1)', clipPath: 'inset(0 0 4% 0 round 0 0 2px 2px)', offset: 0.5 },
            { opacity: 1, transform: 'translateY(0) scaleY(1) scaleX(1)', clipPath: 'inset(0 0 0% 0 round 0 0 2px 2px)' },
          ],
          { duration: 380, easing: 'cubic-bezier(0.32, 0.72, 0, 1)', fill: 'forwards' }
        );
      }
    }

    const trapContainer = this.detailsContainer.querySelector('[tabindex="-1"]');
    const inputFocus = this.detailsContainer.querySelector('input:not([type="hidden"])');
    const focusTarget = inputFocus || trapContainer;
    if (trapContainer && focusTarget) {
      trapFocus(trapContainer, focusTarget);
    }
  }

  close(focusToggle = true) {
    if (this._isClosing) return;
    if (!this.detailsContainer.hasAttribute('open')) return;

    this._isClosing = true;

    /* Remove body click listener immediately to prevent re-entry */
    document.body.removeEventListener('click', this.onBodyClickEvent);

    if (!this._canAnimate()) {
      removeTrapFocus(focusToggle ? this.summaryToggle : null);
      this._finalize();
      return;
    }

    const panel = this._getPanel();
    if (!panel) {
      removeTrapFocus(focusToggle ? this.summaryToggle : null);
      this._finalize();
      return;
    }

    /* Start animation immediately — defer focus cleanup to after animation */
    panel.getAnimations().forEach((a) => a.cancel());

    const anim = panel.animate(
      [
        { opacity: 1, transform: 'translateY(0) scaleY(1) scaleX(1)', clipPath: 'inset(0 0 0% 0 round 0 0 2px 2px)' },
        { opacity: 0, transform: 'translateY(-8px) scaleY(0.94) scaleX(0.99)', clipPath: 'inset(0 0 100% 0 round 0 0 2px 2px)' },
      ],
      { duration: 220, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
    );

    const cleanup = () => {
      try { anim.cancel(); } catch (e) {}
      removeTrapFocus(focusToggle ? this.summaryToggle : null);
      this._finalize();
    };

    anim.finished.then(cleanup).catch(cleanup);
  }

  _finalize() {
    this.detailsContainer.removeAttribute('open');
    if (!this._skipScrollLock) {
      window.JgtScrollLock.unlock();
    }
    this._isClosing = false;
  }
}

customElements.define('details-modal', DetailsModal);
